/**
 * MerakiProvisioningRunner — Phase 5 sub-task.
 *
 * The big one — the page where you actually execute a workflow against a
 * network. Replaces the legacy 2,688-line provisioning.ts + 579-line
 * provisioning.html.
 *
 * Layout:
 *   - Hero with workflow name + back link to the picker
 *   - Compliance score donut + diagnosis summary
 *   - Filter chips for setting state (drift / missing / compliant / …)
 *   - Settings grouped by category — each row shows current vs desired
 *     side-by-side as JSON, with a willPush toggle
 *   - Sticky action bar at bottom: confirm + push CTA + progress dialog
 *
 * Skipped vs. legacy: PDF reference viewer, full history tab. Those land
 * in a follow-up turn if needed.
 */
import { useEffect, useMemo, useReducer, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  PlayCircle,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { PanelSkeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { MerakiSubNav } from "@/components/app/meraki-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { AiExplain } from "@/components/app/ai-explain";
import { useProvisioningRun } from "@/stores/provisioning-run";
import type {
  ProvisioningSetting,
  SettingState,
} from "@/api/schemas/provisioning";
import { ApiError } from "@/api/client";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

/* ============================================ */
/* HELPERS                                      */
/* ============================================ */

function stateStyle(s: SettingState): {
  pill: "ok" | "warn" | "err" | "info" | "neutral";
  label: string;
  icon: React.ReactNode;
} {
  switch (s) {
    case "compliant":
      return {
        pill: "ok",
        label: "compliant",
        icon: <CheckCircle2 size={14} />,
      };
    case "drift":
      return {
        pill: "warn",
        label: "drift",
        icon: <AlertTriangle size={14} />,
      };
    case "missing":
      return { pill: "err", label: "missing", icon: <XCircle size={14} /> };
    case "error":
      return { pill: "err", label: "error", icon: <XCircle size={14} /> };
    case "skipped":
      return { pill: "neutral", label: "skipped", icon: <Clock size={14} /> };
  }
}

const ALL_STATES: SettingState[] = [
  "drift",
  "missing",
  "error",
  "compliant",
  "skipped",
];

/** JSON.stringify with stable formatting for diff cells. */
function pretty(value: unknown): string {
  if (value === undefined || value === null) return "(none)";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/* ============================================ */
/* PAGE                                         */
/* ============================================ */

export function MerakiProvisioningRunner() {
  const animatedRootRef = useUiAutoAnimate();

  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Network id can be passed via ?network=<id>; fall back to the lab default
  // so the runner is fully clickable without the picker.
  const networkId = searchParams.get("network") ?? "L_111111111111111111";
  const workflowSlug = slug ?? "vlan-cleanup-v3";

  const {
    byKey,
    error,
    status,
    load,
    toggleWillPush,
    setAllWillPush,
    provision,
  } = useProvisioningRun();

  useEffect(() => {
    void load(workflowSlug, networkId);
  }, [load, workflowSlug, networkId]);

  const diagnosis = byKey[`${workflowSlug}:${networkId}`];

  // Confirm + push dialog state.
  type PushState = { confirmOpen: boolean; pushOpen: boolean; pushPct: number | undefined; pushMessage: string; pushDone: boolean };
  const [{ confirmOpen, pushOpen, pushPct, pushMessage, pushDone }, setPush] = useReducer(
    (s: PushState, p: Partial<PushState>) => ({ ...s, ...p }),
    { confirmOpen: false, pushOpen: false, pushPct: undefined, pushMessage: "Queueing provision task…", pushDone: false },
  );

  // Filter + expand state.
  const [stateFilters, setStateFilters] = useState<SettingState[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleStateFilter(s: SettingState) {
    setStateFilters((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }

  // Filter + group settings by category.
  const grouped = useMemo(() => {
    if (!diagnosis) return new Map<string, ProvisioningSetting[]>();
    const stateFilterSet = new Set(stateFilters);
    const filtered = diagnosis.settings.filter((s) =>
      stateFilters.length === 0 ? true : stateFilterSet.has(s.state),
    );
    const out = new Map<string, ProvisioningSetting[]>();
    for (const s of filtered) {
      if (!out.has(s.category)) out.set(s.category, []);
      out.get(s.category)?.push(s);
    }
    return out;
  }, [diagnosis, stateFilters]);

  // Counts for the filter chips + KPIs.
  const counts = useMemo(() => {
    const c = {
      total: 0,
      compliant: 0,
      drift: 0,
      missing: 0,
      error: 0,
      skipped: 0,
      willPush: 0,
    };
    if (!diagnosis) return c;
    for (const s of diagnosis.settings) {
      c.total++;
      c[s.state]++;
      if (s.willPush) c.willPush++;
    }
    return c;
  }, [diagnosis]);

  async function startPush() {
    setPush({ confirmOpen: false, pushOpen: true, pushDone: false, pushPct: undefined, pushMessage: "Queueing provision task…" });
    try {
      const final = await provision(workflowSlug, networkId, (p) => {
        if (p.total && p.current !== undefined)
          setPush({ pushPct: (p.current / p.total) * 100 });
        if (p.message) setPush({ pushMessage: p.message });
      });
      if (final.state === "SUCCESS") {
        setPush({ pushDone: true });
        setTimeout(() => setPush({ pushOpen: false }), 1400);
        toast.success("Provision complete");
        // Reload diagnosis so the UI reflects post-push state. Force-bypasses
        // the freshness window since we know the underlying state just changed.
        await load(workflowSlug, networkId, { force: true });
      } else {
        toast.error("Provision failed", {
          description: final.message ?? "Check the audit log.",
        });
        setPush({ pushOpen: false });
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      toast.error("Could not provision", { description: msg });
      setPush({ pushOpen: false });
    }
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6 pb-28">
      {/* Crumb */}
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">
          Meraki
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/meraki/provisioning" className="text-fg-dim hover:text-fg">
          Provisioning
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">
          {diagnosis?.workflowName ?? workflowSlug}
        </span>
      </div>

      <MerakiSubNav />

      <NoVendorKeysBanner vendor="meraki" />

      {/* Hero */}
      <div className="flex items-end justify-between gap-6">
        <div className="min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/meraki/provisioning")}
            className="mb-2 -ml-2"
          >
            <ArrowLeft size={13} />
            Back to workflows
          </Button>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            {diagnosis?.workflowName ?? workflowSlug}
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Diagnosis for{" "}
            <b className="font-mono text-fg">
              {diagnosis?.networkName ?? networkId}
            </b>{" "}
            — review each drifted setting, untoggle anything you don't want
            pushed, then click Push to provision.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void load(workflowSlug, networkId, { force: true })}
            disabled={status === "loading"}
          >
            <RefreshCw size={13} />
            Re-diagnose
          </Button>
        </div>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      {/* Compliance score + KPIs */}
      <Card>
        <CardContent className="grid grid-cols-1 gap-5 lg:grid-cols-[260px_1fr]">
          <ComplianceScore score={diagnosis?.score ?? 0} />
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <KpiTile label="Total settings" value={counts.total} pill="info" />
            <KpiTile label="Compliant" value={counts.compliant} pill="ok" />
            <KpiTile label="Drift" value={counts.drift} pill="warn" />
            <KpiTile label="Missing" value={counts.missing} pill="err" />
            <KpiTile
              label="Will push"
              value={counts.willPush}
              pill="info"
              highlight
            />
          </div>
        </CardContent>
      </Card>

      {/* AI summary of the whole diagnosis. Only rendered once we have a
          loaded diagnosis (no point asking the model about an empty result). */}
      {diagnosis && diagnosis.settings.length > 0 ? (
        <AiExplain
          kind="diagnosis-summary"
          context={diagnosis as unknown as Record<string, unknown>}
        />
      ) : null}

      {/* Filter chips + bulk toggles */}
      <ProvisioningFilterBar
        stateFilters={stateFilters}
        counts={counts}
        toggleStateFilter={toggleStateFilter}
        onPushAll={() => setAllWillPush(workflowSlug, networkId, true)}
        onPushNone={() => setAllWillPush(workflowSlug, networkId, false)}
      />

      {/* Grouped settings list */}
      {!diagnosis ? (
        <div className="flex flex-col gap-3">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>
      ) : grouped.size === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <ShieldCheck size={20} className="text-mint" />
            <CardTitle>Nothing matches your filter</CardTitle>
            <CardSub>Widen the filter chips above or clear them.</CardSub>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          {Array.from(grouped.entries()).map(([category, settings]) => (
            <div key={category} className="flex flex-col gap-2">
              <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-fg-mute">
                <span>{category}</span>
                <span className="h-px flex-1 bg-line" />
                <span>
                  {settings.length} setting{settings.length === 1 ? "" : "s"}
                </span>
              </div>
              <Card>
                <ul className="m-0 flex list-none flex-col p-0">
                  {settings.map((s) => (
                    <SettingRow
                      key={s.id}
                      setting={s}
                      workflowName={diagnosis?.workflowName ?? workflowSlug}
                      expanded={expanded.has(s.id)}
                      onToggleExpanded={() => toggleExpanded(s.id)}
                      onToggleWillPush={() =>
                        toggleWillPush(workflowSlug, networkId, s.id)
                      }
                    />
                  ))}
                </ul>
              </Card>
            </div>
          ))}
        </div>
      )}

      <ProvisioningActionBar
        willPushCount={counts.willPush}
        onOpen={() => setPush({ confirmOpen: true })}
      />

      <ProvisioningPushDialogs
        confirmOpen={confirmOpen}
        setConfirmOpen={(v) => setPush({ confirmOpen: v })}
        pushOpen={pushOpen}
        setPushOpen={(v) => setPush({ pushOpen: v })}
        pushDone={pushDone}
        pushPct={pushPct}
        pushMessage={pushMessage}
        willPushCount={counts.willPush}
        networkName={diagnosis?.networkName ?? networkId}
        startPush={() => void startPush()}
      />
    </div>
  );
}

function ProvisioningFilterBar({
  stateFilters,
  counts,
  toggleStateFilter,
  onPushAll,
  onPushNone,
}: {
  stateFilters: SettingState[];
  counts: Record<SettingState, number>;
  toggleStateFilter: (s: SettingState) => void;
  onPushAll: () => void;
  onPushNone: () => void;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-fg-mute">
            Show
          </span>
          {ALL_STATES.map((s) => {
            const active = stateFilters.includes(s);
            const sty = stateStyle(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => toggleStateFilter(s)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                  active
                    ? "border-violet/40 bg-aurora-soft text-violet"
                    : "border-line bg-surface text-fg-mute hover:bg-surface-2 hover:text-fg",
                )}
              >
                {sty.icon}
                {sty.label}
                <span className="font-mono opacity-70">{counts[s]}</span>
              </button>
            );
          })}
          <span className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onPushAll}>
              Push all drifted
            </Button>
            <Button size="sm" variant="ghost" onClick={onPushNone}>
              Push none
            </Button>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function ProvisioningActionBar({
  willPushCount,
  onOpen,
}: {
  willPushCount: number;
  onOpen: () => void;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-bg-0/85 px-7 py-3 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4">
        <div className="flex items-center gap-3 text-xs text-fg-dim">
          <span>
            <b className="font-mono text-fg">{willPushCount}</b> setting
            {willPushCount === 1 ? "" : "s"} staged for push
          </span>
          {willPushCount === 0 ? (
            <Badge variant="ok" size="sm">nothing to do</Badge>
          ) : (
            <Badge variant="warn" size="sm" dot>destructive</Badge>
          )}
        </div>
        <Button onClick={onOpen} disabled={willPushCount === 0}>
          <PlayCircle size={14} />
          Push{" "}
          {willPushCount > 0
            ? `${willPushCount} change${willPushCount === 1 ? "" : "s"}`
            : ""}
          <ArrowRight size={13} />
        </Button>
      </div>
    </div>
  );
}

function ProvisioningPushDialogs({
  confirmOpen,
  setConfirmOpen,
  pushOpen,
  setPushOpen,
  pushDone,
  pushPct,
  pushMessage,
  willPushCount,
  networkName,
  startPush,
}: {
  confirmOpen: boolean;
  setConfirmOpen: (v: boolean) => void;
  pushOpen: boolean;
  setPushOpen: (v: boolean) => void;
  pushDone: boolean;
  pushPct: number | undefined;
  pushMessage: string;
  willPushCount: number;
  networkName: string;
  startPush: () => void;
}) {
  return (
    <>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogTitle>Push provisioning?</DialogTitle>
          <DialogDescription className="mt-2">
            <span>About to apply</span>{" "}
            <b className="font-mono text-fg">{willPushCount}</b>{" "}
            <span>setting change{willPushCount === 1 ? "" : "s"} to</span>
            <span className="mt-2 block rounded-lg border border-line bg-surface px-3 py-2 font-mono text-sm text-fg">
              {networkName}
            </span>
            <span className="mt-3 block">
              The change is logged in the audit trail. Each setting change is
              independent — partial failures don't roll the others back.
            </span>
          </DialogDescription>
          <div className="mt-5 flex justify-end gap-2.5">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button onClick={startPush}>
              <PlayCircle size={14} />
              Confirm &amp; push
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent>
          {pushDone ? (
            <div className="flex flex-col items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint/15 text-mint">
                <CheckCircle2 size={22} />
              </div>
              <DialogTitle>Provision complete</DialogTitle>
              <DialogDescription>
                Re-running diagnosis to confirm. Closing in a moment…
              </DialogDescription>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <DialogTitle>Provisioning {networkName}</DialogTitle>
              <DialogDescription>{pushMessage}</DialogDescription>
              <Progress
                value={pushPct}
                hint={pushPct === undefined ? "Establishing connection…" : undefined}
              />
              <div className="text-[11px] text-fg-mute">
                Closing this dialog won't cancel the provision — it continues in
                the background.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ============================================ */
/* COMPLIANCE SCORE — donut SVG                 */
/* ============================================ */

function ComplianceScore({ score }: { score: number }) {
  const pct = Math.max(0, Math.min(100, score));
  // Donut math: stroke-dasharray on a 100-circumference circle.
  const circumference = 100;
  const dash = (pct / 100) * circumference;
  const tone =
    pct >= 95
      ? { stroke: "url(#cs-grad-ok)", text: "text-mint", label: "compliant" }
      : pct >= 70
        ? { stroke: "url(#cs-grad-warn)", text: "text-peach", label: "drift" }
        : {
            stroke: "url(#cs-grad-err)",
            text: "text-rose",
            label: "needs work",
          };

  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 36 36" width="120" height="120">
        <defs>
          <linearGradient id="cs-grad-ok" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#63ffc8" />
            <stop offset="100%" stopColor="#4ec6ff" />
          </linearGradient>
          <linearGradient id="cs-grad-warn" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ffb478" />
            <stop offset="100%" stopColor="#7d6cff" />
          </linearGradient>
          <linearGradient id="cs-grad-err" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#ff7aa8" />
            <stop offset="100%" stopColor="#ffb478" />
          </linearGradient>
        </defs>
        {/* track */}
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          stroke="rgb(var(--line))"
          strokeWidth="3"
        />
        {/* progress */}
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          fill="none"
          stroke={tone.stroke}
          strokeWidth="3"
          strokeDasharray={`${dash} ${circumference - dash}`}
          strokeDashoffset="25" /* start at 12 o'clock */
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
        />
      </svg>
      <div>
        <div
          className={cn("text-4xl font-extrabold tracking-tight", tone.text)}
        >
          {Math.round(pct)}
          <span className="text-lg text-fg-mute">%</span>
        </div>
        <div className="text-[11px] text-fg-mute">
          compliance · {tone.label}
        </div>
      </div>
    </div>
  );
}

/* ============================================ */
/* KPI TILE                                     */
/* ============================================ */

function KpiTile({
  label,
  value,
  pill,
  highlight,
}: {
  label: string;
  value: number;
  pill: "ok" | "warn" | "err" | "info" | "neutral";
  highlight?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-line bg-surface px-3.5 py-3",
        highlight && "border-violet/40 bg-aurora-soft",
      )}
    >
      <div className="flex items-center justify-between text-[11px] text-fg-mute">
        {label}
      </div>
      <div className="mt-1.5 flex items-end gap-2">
        <div className="text-2xl font-extrabold tracking-tight text-fg">
          {value}
        </div>
        {value > 0 ? (
          <Badge variant={pill} size="sm">
            {pill === "info" ? "ready" : pill}
          </Badge>
        ) : null}
      </div>
    </div>
  );
}

/* ============================================ */
/* SETTING ROW                                  */
/* ============================================ */

function SettingRow({
  setting,
  workflowName,
  expanded,
  onToggleExpanded,
  onToggleWillPush,
}: {
  setting: ProvisioningSetting;
  /** Passed through to the AiExplain context so the prompt has workflow name. */
  workflowName: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleWillPush: () => void;
}) {
  const sty = stateStyle(setting.state);
  const isCompliant =
    setting.state === "compliant" || setting.state === "skipped";
  return (
    <li className="border-t border-line/60 first:border-t-0">
      <div className="grid grid-cols-[28px_28px_1fr_120px_auto_auto] items-center gap-3 px-5 py-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-label={expanded ? "Collapse" : "Expand"}
          className="text-fg-mute hover:text-fg"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span
          className={cn(
            "inline-flex h-6 w-6 items-center justify-center rounded-full",
            sty.pill === "ok" && "bg-mint/15 text-mint",
            sty.pill === "warn" && "bg-peach/15 text-peach",
            sty.pill === "err" && "bg-rose/15 text-rose",
            sty.pill === "info" && "bg-violet/15 text-violet",
            sty.pill === "neutral" && "bg-line/30 text-fg-mute",
          )}
        >
          {sty.icon}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-fg">
            {setting.name}
          </div>
          <div className="font-mono text-[10.5px] text-fg-mute">
            {setting.path}
          </div>
          {setting.note ? (
            <div className="mt-0.5 text-[11px] text-fg-dim">{setting.note}</div>
          ) : null}
        </div>
        <Badge variant={sty.pill} size="sm">
          {sty.label}
        </Badge>
        <label
          className={cn(
            "inline-flex items-center gap-2 text-[11px]",
            isCompliant
              ? "cursor-not-allowed text-fg-mute opacity-60"
              : "cursor-pointer text-fg-dim",
          )}
          title={isCompliant ? "Nothing to push" : "Include in next push"}
        >
          <input
            type="checkbox"
            disabled={isCompliant}
            checked={setting.willPush}
            onChange={onToggleWillPush}
            className="accent-violet"
          />
          push
        </label>
        <span />
      </div>
      {expanded ? (
        <div className="border-t border-line/60 bg-surface-tint/40 px-5 py-3">
          <div className="grid grid-cols-2 gap-3">
            <DiffPane title="Current" tone="rose" value={setting.current} />
            <DiffPane title="Desired" tone="mint" value={setting.desired} />
          </div>
          {/* AI explanation lives below the diff so the user sees the raw
              data first, then the narration. Lazy-loaded — no token spend
              unless the user actually expands the row and clicks the button. */}
          <div className="mt-3">
            <AiExplain
              kind="diagnosis-setting"
              context={{ ...setting, workflowName }}
            />
          </div>
        </div>
      ) : null}
    </li>
  );
}

/* ============================================ */
/* DIFF PANE                                    */
/* ============================================ */

function DiffPane({
  title,
  tone,
  value,
}: {
  title: string;
  tone: "rose" | "mint";
  value: unknown;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border bg-bg-0",
        tone === "rose" && "border-rose/30",
        tone === "mint" && "border-mint/30",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-between border-b px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.1em]",
          tone === "rose" && "border-rose/20 bg-rose/[0.05] text-rose",
          tone === "mint" && "border-mint/20 bg-mint/[0.05] text-mint",
        )}
      >
        <span>{title}</span>
        <span className="opacity-60">
          {typeof value === "object" && value !== null ? "json" : "value"}
        </span>
      </div>
      <pre className="m-0 max-h-[260px] overflow-auto p-3 font-mono text-[11px] leading-relaxed text-fg">
        {pretty(value)}
      </pre>
    </div>
  );
}
